/** Pure pixel sizing for the ordered desktop panel row. */

import { clamp } from '@/lib/utils';
import type { AllocatedPanel, OpenPanel, PanelWidth, UserWidths } from '@/types/panels';
import { MAX_PANEL_WIDTH } from '@/types/panels';

/** A panel sized but not yet placed; positionPanels adds the start. */
type SizedPanel = Omit<AllocatedPanel, 'startPx'>;

export const PANEL_PADDING_PX = 16;
// A phone panel is the whole screen.
export const PHONE_PADDING_PX = 0;
export const PANEL_GAP_PX = 8;

const EPS = 1e-6;

/** Row position of a panel that states none: after the list panels, before the fill surface. */
export const DEFAULT_PANEL_ORDER = 50;

/** Width rules a panel config leaves unsaid — definePanel fills them in from here. */
export const DEFAULT_WIDTH: PanelWidth = Object.freeze({
    min: 288, default: 384, max: Infinity, fill: false, keepsDefaultWidth: false,
});

function compareByOrder(a: OpenPanel, b: OpenPanel): number {
    return (a.config.order ?? DEFAULT_PANEL_ORDER) - (b.config.order ?? DEFAULT_PANEL_ORDER);
}

function getWidthRules(panel: OpenPanel, row: OpenPanel[]): PanelWidth {
    return {
        ...DEFAULT_WIDTH,
        ...(panel.config.width ?? {}),
        ...(panel.config.getWidth?.(panel.props, row) ?? {}),
    };
}

/**
 * The width a panel takes on a stage with room to spare: the one the user
 * dragged it to, else its declared default.
 *
 * A `fill` panel is excluded: it is whatever the row leaves over, so it has no
 * width of its own and a value left in storage by an older build would only
 * seed a number it never keeps. An expanded panel is sized by allocate itself.
 */
function getPreferredWidth(
    panel: OpenPanel, row: OpenPanel[], userWidths: UserWidths,
): number {
    const rules = getWidthRules(panel, row);
    const savedPanelWidth = rules.fill ? undefined : userWidths[panel.id];
    const seed = typeof savedPanelWidth === 'number' &&
        Number.isFinite(savedPanelWidth) && savedPanelWidth > 0
        ? savedPanelWidth
        : rules.default;
    return clamp(seed, rules.min, rules.max);
}

export function getPanelWidthBudget(stageWidth: number, count: number): number {
    if (count <= 0) {
        return 0;
    }
    const gaps = Math.max(0, count - 1) * PANEL_GAP_PX;
    return Math.max(0, stageWidth - 2 * PANEL_PADDING_PX - gaps);
}

/**
 * Keep the longest run that fits from `start`, on two budgets running in step.
 * `used` is the honest sum of minimums — nothing is ever kept past it.
 * `reservedPx` charges a `keepsDefaultWidth` panel the width it asked for instead of its
 * minimum, so a panel to its right is dropped rather than squeezing a form
 * that declared it needs the room (camera_settings: the video preview goes,
 * the 800px form stays whole).
 *
 * The focused panel — the one the user last opened — overrules the reserve,
 * never `used`. Asking for a surface has to be able to summon it back, and
 * the hold then simply gives way in the allocator like any other width.
 *
 * The panel at `start` is admitted whatever it measures, so the stage is never
 * empty and the run below always reaches the panel it is looking for.
 */
function takeRun(
    orderedOpenPanels: OpenPanel[], start: number, contentWidth: number,
    userWidths: UserWidths, focusedId: string | null,
): OpenPanel[] {
    const visibleOpenPanels: OpenPanel[] = [];
    let used = 0;
    let reservedPx = 0;
    let isOverruled = false;

    for (const panel of orderedOpenPanels.slice(start)) {
        const rules = getWidthRules(panel, orderedOpenPanels);
        const gap = visibleOpenPanels.length ? PANEL_GAP_PX : 0;
        if (visibleOpenPanels.length) {
            if (used + gap + rules.min > contentWidth + EPS) {
                break;
            }
            if (!isOverruled && reservedPx + gap + rules.min > contentWidth + EPS) {
                if (panel.id !== focusedId) {
                    break;
                }
                isOverruled = true;
            }
        }
        visibleOpenPanels.push(panel);
        used += gap + rules.min;
        reservedPx = isOverruled
            ? used
            : reservedPx + gap +
                (rules.keepsDefaultWidth ? getPreferredWidth(panel, orderedOpenPanels,
                    userWidths) : rules.min);
    }
    return visibleOpenPanels;
}

interface SelectVisibleOpts {
    openPanels: OpenPanel[];
    stageWidth: number;
    userWidths?: UserWidths;
    focusedId?: string | null;
}

/**
 * What the stage shows: the longest contiguous run of the ordered row that
 * fits AND holds the panel the user last opened.
 *
 * The run starts at the left edge whenever it can, so panels ordinarily shed
 * from the right and come back in order as space returns. When the panel just
 * opened lies past that fold, the run slides right by the least it takes to
 * reach it — on a stage this narrow a click has to produce the surface it
 * asked for, and closing that surface hands the stage back to the panels it
 * displaced.
 *
 * An overlay panel joins the run only where it fits beside it with every panel
 * of the run at the width it has without it; elsewhere it floats over the run
 * (`selectOverlays`).
 */
export function selectVisible({
    openPanels, stageWidth, userWidths = {}, focusedId = null,
}: SelectVisibleOpts): OpenPanel[] {
    const dockedOpenPanels = openPanels.filter((panel) => !panel.config.isOverlay);
    let row = selectRun(dockedOpenPanels, stageWidth, userWidths, focusedId);
    for (const panel of openPanels.filter((candidate) => candidate.config.isOverlay)) {
        const joinedOpenPanels = selectRun([...row, panel], stageWidth, userWidths, focusedId);
        // An overlay panel shows either way, so a run that sheds or narrows a panel for it is
        // refused.
        if (joinedOpenPanels.length === row.length + 1 && keepsWidths(row,
            joinedOpenPanels, stageWidth, userWidths)) {
            row = joinedOpenPanels;
        }
    }
    return row;
}

/**
 * Whether each panel of `row` has the same width in `joinedOpenPanels`, the row with a panel
 * added.
 */
function keepsWidths(
    row: OpenPanel[], joinedOpenPanels: OpenPanel[], stageWidth: number, userWidths: UserWidths,
): boolean {
    const afterAllocatedPanels = allocate({ openPanels: joinedOpenPanels, stageWidth,
        userWidths });
    return allocate({ openPanels: row, stageWidth, userWidths }).every((panel) => {
        const width = afterAllocatedPanels.find((other) => other.id === panel.id)?.widthPx;
        return width !== undefined && Math.abs(width - panel.widthPx) < EPS;
    });
}

/** The open overlay panels the row has no room for. They float over it instead of being shed. */
export function selectOverlays(
    { openPanels, visiblePanels }: { openPanels: OpenPanel[]; visiblePanels: OpenPanel[] },
): OpenPanel[] {
    return openPanels.filter((panel) => (
        panel.config.isOverlay && !visiblePanels.some((shown) => shown.id === panel.id)
    ));
}

function selectRun(
    openPanels: OpenPanel[], stageWidth: number, userWidths: UserWidths, focusedId: string | null,
): OpenPanel[] {
    const orderedOpenPanels = [...openPanels].sort(compareByOrder);
    const contentWidth = Math.max(0, stageWidth - 2 * PANEL_PADDING_PX);
    const focusIndex = orderedOpenPanels.findIndex((panel) => panel.id === focusedId);

    for (let start = 0; start < focusIndex; start += 1) {
        const run = takeRun(orderedOpenPanels, start, contentWidth, userWidths, focusedId);
        if (run.some((panel) => panel.id === focusedId)) {
            return run;
        }
    }
    // No focus (or it leads the row): the plain left-to-right prefix. Otherwise
    // the run anchored on the focused panel, which always holds it.
    return takeRun(orderedOpenPanels, Math.max(0, focusIndex), contentWidth, userWidths,
        focusedId);
}

/**
 * Would `candidate` be shown at all if it joined `openPanels` on this stage?
 * The stage's own answer, reserves and all, so an automatic open (an auto-open panel,
 * a preview a tab expects) can decline rather than file a panel where the user
 * cannot see it. An unmeasured stage admits it — the measurement arrives with
 * the first layout, and withholding panels before it would be a guess.
 */
export function fitsBeside({
    openPanels, candidate, stageWidth, userWidths = {}, focusedId = null,
}: SelectVisibleOpts & { candidate: OpenPanel }): boolean {
    if (!(stageWidth > 0)) {
        return true;
    }
    return selectVisible({
        openPanels: [...openPanels, candidate], stageWidth, userWidths, focusedId,
    }).some((panel) => panel.id === candidate.id);
}

export function selectVisiblePhone(
    { openPanels, focusedId }: { openPanels: OpenPanel[]; focusedId?: string | null },
): OpenPanel[] {
    const orderedOpenPanels = [...openPanels].sort(compareByOrder);
    const shown = orderedOpenPanels.find((panel) =>
        panel.id === focusedId) ?? orderedOpenPanels.at(-1);
    return shown ? [shown] : [];
}

/** Add cumulative pixel starts to an already ordered set of sized panels. */
export function positionPanels<T extends { widthPx: number }>(
    panels: T[],
): (T & { startPx: number })[] {
    let startPx = 0;
    return panels.map((panel) => {
        const positionedValue = { ...panel, startPx };
        startPx += panel.widthPx + PANEL_GAP_PX;
        return positionedValue;
    });
}

/**
 * Shrink `pool` proportionally until every item in `items` fits `budget`,
 * pinning each at its own min as it bottoms out. Items outside `pool` keep
 * whatever width they already have and are charged against the budget as-is.
 *
 * Returns true once the row fits, false when the whole pool reached its
 * minimums and the shortfall has to come from somewhere else.
 */
function shrinkPool(items: SizedPanel[], pool: SizedPanel[], budget: number): boolean {
    let activePanels = pool.slice();
    while (activePanels.length) {
        const fixedWidthPx = items
            .filter((item) => !activePanels.includes(item))
            .reduce((sum, item) => sum + item.widthPx, 0);
        const activeTotal = activePanels.reduce((sum, item) => sum + item.widthPx, 0);
        const target = budget - fixedWidthPx;
        if (target >= activeTotal - EPS) {
            return true; //< already fits; take nothing
        }

        const scale = activeTotal > EPS ? target / activeTotal : 0;
        const clampedPanels = activePanels.filter((item) => item.widthPx * scale < item.min - EPS);
        if (clampedPanels.length === 0) {
            for (const item of activePanels) {
                item.widthPx *= scale;
            }
            return true;
        }
        for (const item of clampedPanels) {
            item.widthPx = item.min;
        }
        activePanels = activePanels.filter((item) => !clampedPanels.includes(item));
    }
    return false;
}

/**
 * Take width back when the stage tightens (a window resize, the chat drawer
 * opening). Fill panels give first and non-fill panels only once every fill
 * panel is pinned at its min — `fill` marks the elastic surface that absorbs
 * both free width and pressure, so a panel that opted out keeps the width it was
 * given until the geometry genuinely cannot honour it. Without that ordering a
 * "fixed" panel still drifted on every resize, because the proportional pass
 * charged it the same share as the elastic one beside it.
 *
 * When even the minimums don't fit there is no allocation to make — squeeze
 * everything by the same factor rather than emptying the stage.
 */
function shrinkToBudget(items: SizedPanel[], budget: number): void {
    const minTotal = items.reduce((sum, item) => sum + item.min, 0);
    if (minTotal > budget + EPS) {
        const scale = minTotal > EPS ? budget / minTotal : 0;
        for (const item of items) {
            item.widthPx = item.min * scale;
        }
        return;
    }

    const fill = items.filter((item) => item.fill);
    if (shrinkPool(items, fill, budget)) {
        return;
    }
    shrinkPool(items, items.filter((item) => !item.fill), budget);
}

/**
 * Hand `pool` the free width left in `budget`, proportionally, pinning each item at
 * its own max as it tops out. Items outside `pool` keep the width they have.
 *
 * Returns true when the free width is spent (or there was none), false when the
 * whole pool reached its maximums and space is still going begging.
 */
function growPool(items: SizedPanel[], pool: SizedPanel[], budget: number): boolean {
    let activePanels = pool.filter((item) => item.widthPx < item.max - EPS);
    while (activePanels.length) {
        const fixedWidthPx = items
            .filter((item) => !activePanels.includes(item))
            .reduce((sum, item) => sum + item.widthPx, 0);
        const activeTotal = activePanels.reduce((sum, item) => sum + item.widthPx, 0);
        const target = budget - fixedWidthPx;
        if (target <= activeTotal + EPS || activeTotal <= EPS) {
            return true; //< no free width
        }

        const scale = target / activeTotal;
        const clampedPanels = activePanels.filter((item) => item.widthPx * scale > item.max + EPS);
        if (clampedPanels.length === 0) {
            for (const item of activePanels) {
                item.widthPx *= scale;
            }
            return true;
        }
        for (const item of clampedPanels) {
            item.widthPx = item.max;
        }
        activePanels = activePanels.filter((item) => !clampedPanels.includes(item));
    }
    return false;
}

export function allocate({
    openPanels, stageWidth, userWidths = {},
}: {
    openPanels: OpenPanel[];
    stageWidth: number;
    userWidths?: UserWidths;
}): AllocatedPanel[] {
    if (openPanels.length === 0) {
        return [];
    }
    const orderedOpenPanels = [...openPanels].sort(compareByOrder);
    const budget = getPanelWidthBudget(stageWidth, orderedOpenPanels.length);
    const items = orderedOpenPanels.map((panel) => {
        const width = getWidthRules(panel, orderedOpenPanels);
        return {
            ...panel,
            min: width.min,
            max: width.max,
            fill: !!width.fill,
            isExpanded: userWidths[panel.id] === MAX_PANEL_WIDTH,
            widthPx: getPreferredWidth(panel, orderedOpenPanels, userWidths),
        };
    });

    // An expanded panel asked for all the room there is, so the fill panels
    // beside it keep only their min until it reaches its own max.
    const expandedValue = items.filter((item) => item.isExpanded);
    if (expandedValue.length) {
        for (const item of items) {
            if (item.fill || item.isExpanded) {
                item.widthPx = item.min;
            }
        }
    }

    // Room to spare goes to expanded and `fill` panels alone. Every other panel
    // is at the width it was set to, and a roomy stage is no reason to change
    // it: the row ends short and the remainder stays trailing, where the drag
    // handle on each panel's right edge can claim it (see panelResize.ts).
    // Growing every panel to its `max` instead is what used to erase a dragged
    // width the moment a neighbour closed.
    const getTotal = (): number => items.reduce((sum, item) => sum + item.widthPx, 0);
    if (getTotal() > budget + EPS) {
        shrinkToBudget(items, budget);
    } else if (getTotal() < budget - EPS && !growPool(items, expandedValue, budget)) {
        growPool(items, items.filter((item) => item.fill), budget);
    }

    return positionPanels(items);
}

/** Phone mode owns the whole stage and bypasses desktop min/max allocation. */
export function allocatePhone(
    openPanels: OpenPanel[], stageWidth: number,
): AllocatedPanel[] {
    const widthPx = Math.max(0, stageWidth - 2 * PHONE_PADDING_PX);
    return positionPanels(openPanels.map((panel) => ({
        ...panel,
        min: widthPx,
        max: widthPx,
        fill: true,
        isExpanded: false,
        widthPx,
    })));
}
